import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  AlertTriangle, TicketCheck, FileText, TrendingUp, DollarSign, RefreshCcw,
  ArrowRight, Clock, CheckCircle2, Trophy, Send, Loader2,
} from "lucide-react";

const brl = (v) =>
  `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MEDALHAS = { 1: "🥇", 2: "🥈", 3: "🥉", 4: "4️⃣", 5: "5️⃣" };

const BigCard = ({ icon: Icon, title, value, subtitle, color, accent, link, linkLabel, testId, children }) => (
  <Card
    className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all"
    style={{ background: `linear-gradient(135deg, ${color}10 0%, white 60%)` }}
    data-testid={testId}
  >
    <div className="absolute top-0 left-0 w-1 h-full" style={{ background: color }} />
    <CardHeader className="pb-2">
      <div className="flex items-start justify-between">
        <CardTitle className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">
          {title}
        </CardTitle>
        <div
          className="h-9 w-9 rounded-lg flex items-center justify-center"
          style={{ background: `${color}1a`, color: color }}
        >
          <Icon size={18} />
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-1">
      <div className="text-4xl font-bold" style={{ color }}>{value}</div>
      {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
      {accent && (
        <Badge variant="outline" className="mt-2 text-[10px]" style={{ borderColor: color, color }}>
          {accent}
        </Badge>
      )}
      {children}
      {link && (
        <Link to={link}>
          <Button variant="ghost" size="sm" className="text-xs mt-3 p-0 h-auto hover:bg-transparent" style={{ color }}>
            {linkLabel || "Ver detalhes"} <ArrowRight size={11} className="ml-1" />
          </Button>
        </Link>
      )}
    </CardContent>
  </Card>
);

export default function DashboardExecutivo() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ranking, setRanking] = useState(null);
  const [periodoRanking, setPeriodoRanking] = useState("semana");
  const [loadingRanking, setLoadingRanking] = useState(false);
  const [disparando, setDisparando] = useState(false);

  const isDiretor = user?.role === "diretor";

  const carregar = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/kpis/executivo`);
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  const carregarRanking = async (periodo) => {
    setLoadingRanking(true);
    try {
      const r = await api.get(`/comissoes/ranking-top5?periodo=${periodo}`);
      setRanking(r.data);
    } catch (e) {
      console.error("Falha ranking", e);
    } finally {
      setLoadingRanking(false);
    }
  };

  const dispararRelatorio = async () => {
    if (!confirm("Disparar o Relatório Executivo Semanal agora? Será enviado por e-mail a diretores e gerentes.")) return;
    setDisparando(true);
    try {
      const r = await api.post(`/jobs/executar/relatorio_semanal_executivo`);
      const s = r.data?.result?.summary || {};
      toast.success(`Relatório enviado a ${s.enviados || 0} destinatário(s).`, {
        description: `Top-5: ${s.top5_qtd || 0} · OS: ${s.qtd_os || 0} · SLA vencidos: ${s.tickets_vencidos || 0}`,
      });
    } catch (e) {
      toast.error("Falha ao disparar relatório", { description: e?.response?.data?.detail || e.message });
    } finally {
      setDisparando(false);
    }
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { carregarRanking(periodoRanking); /* eslint-disable-next-line */ }, [periodoRanking]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-slate-400">Carregando KPIs executivos...</div>
      </div>
    );
  }

  const tk = data.tickets || {};
  const os = data.ordens_servico || {};
  const conv = data.conversao || {};
  const com = data.comissoes || {};
  const mesAtual = new Date().toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

  return (
    <div className="space-y-6" data-testid="dashboard-executivo-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            Dashboard <span className="text-orange-500">Executivo</span>
          </h1>
          <p className="text-sm text-slate-500 mt-1 capitalize">
            Período: {mesAtual} · Olá, {user?.name?.split(" ")[0]}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={carregar} data-testid="kpis-refresh-btn">
          <RefreshCcw size={13} className="mr-2" /> Atualizar
        </Button>
      </header>

      {/* 4 KPIs principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <BigCard
          testId="kpi-tickets"
          icon={TicketCheck}
          title="Tickets em Aberto"
          value={tk.abertos || 0}
          subtitle={`${tk.sla_vencidos || 0} vencidos · ${tk.sla_em_risco || 0} em risco`}
          accent={tk.sla_vencidos > 0 ? "⚠️ Atenção SLA" : null}
          color={tk.sla_vencidos > 0 ? "#dc2626" : tk.sla_em_risco > 0 ? "#f59e0b" : "#0ea5e9"}
          link="/app/tickets-ops"
          linkLabel="Ver tickets"
        />

        <BigCard
          testId="kpi-os"
          icon={FileText}
          title="OS do Mês"
          value={os.qtd_mes || 0}
          subtitle={`${brl(os.valor_total_mes)} faturado · ${os.qtd_concluidas_mes || 0} concluídas`}
          color="#0d9488"
          link="/app/posvenda/ordens-servico"
          linkLabel="Ver OS"
        />

        <BigCard
          testId="kpi-conversao"
          icon={TrendingUp}
          title="Conversão Proposta → OS"
          value={`${conv.taxa_conversao_pct || 0}%`}
          subtitle={`${conv.propostas_com_os_mes || 0} de ${conv.propostas_mes || 0} propostas viraram OS`}
          color="#7c3aed"
          link="/app/posvenda/propostas"
          linkLabel="Ver propostas"
        />

        <BigCard
          testId="kpi-comissoes"
          icon={DollarSign}
          title="Comissões Pós-venda (3%)"
          value={brl(com.valor_total_mes)}
          subtitle={`${brl(com.valor_pendente_mes)} pendente · ${brl(com.valor_pago_mes)} pago`}
          color="#ea580c"
          link="/app/posvenda/comissoes"
          linkLabel="Ver comissões"
        />
      </div>

      {/* Visão detalhada (sub-cards) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-red-700">
              <AlertTriangle size={16} /> SLA Operacional
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Vencidos:</span>
              <span className="font-bold text-red-600">{tk.sla_vencidos || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Em risco (&lt; 12h):</span>
              <span className="font-bold text-amber-600">{tk.sla_em_risco || 0}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="text-slate-500">Total ativos:</span>
              <span className="font-bold text-slate-900">{tk.abertos || 0}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-teal-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-teal-700">
              <CheckCircle2 size={16} /> Produtividade Pós-venda
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Propostas no mês:</span>
              <span className="font-bold">{conv.propostas_mes || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Geraram OS:</span>
              <span className="font-bold text-emerald-600">{conv.propostas_com_os_mes || 0}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="text-slate-500">Taxa conversão:</span>
              <span className="font-bold text-violet-600">{conv.taxa_conversao_pct || 0}%</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-orange-700">
              <Clock size={16} /> Comissões aguardando pagamento
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Pendentes:</span>
              <span className="font-bold text-amber-600">{brl(com.valor_pendente_mes)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Já pagas:</span>
              <span className="font-bold text-emerald-600">{brl(com.valor_pago_mes)}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="text-slate-500">Comissões geradas:</span>
              <span className="font-bold">{com.qtd_mes || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="text-xs text-slate-400 text-right">
        Última atualização: {new Date(data.atualizado_em).toLocaleString("pt-BR")}
      </div>

      {/* Top-5 Vendedores por Comissão */}
      <Card className="border-0 shadow-lg overflow-hidden" data-testid="top5-card">
        <div className="h-1.5" style={{ background: "linear-gradient(90deg, #1B3A6B 0%, #F97316 100%)" }} />
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#1B3A6B] to-[#F97316] text-white">
                <Trophy size={20} />
              </div>
              <div>
                <CardTitle className="text-lg font-bold text-slate-900">
                  Top 5 Vendedores por Comissão
                </CardTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  Critério: comissões <strong>aprovadas + pagas</strong> no período
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-testid="ranking-periodo-toggle">
                <button
                  onClick={() => setPeriodoRanking("semana")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                    periodoRanking === "semana"
                      ? "bg-white shadow text-[#1B3A6B]"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid="ranking-toggle-semana"
                >
                  Semana
                </button>
                <button
                  onClick={() => setPeriodoRanking("mes")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                    periodoRanking === "mes"
                      ? "bg-white shadow text-[#1B3A6B]"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid="ranking-toggle-mes"
                >
                  Mês
                </button>
              </div>
              {isDiretor && (
                <Button
                  size="sm"
                  onClick={dispararRelatorio}
                  disabled={disparando}
                  className="bg-[#F97316] hover:bg-[#ea580c] text-white"
                  data-testid="disparar-relatorio-btn"
                >
                  {disparando ? (
                    <><Loader2 size={13} className="mr-2 animate-spin" />Disparando...</>
                  ) : (
                    <><Send size={13} className="mr-2" />Disparar Relatório Agora</>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {loadingRanking && (
            <div className="py-8 text-center text-slate-400 text-sm" data-testid="ranking-loading">
              <Loader2 className="animate-spin inline mr-2" size={14} /> Carregando ranking…
            </div>
          )}
          {!loadingRanking && ranking && ranking.items?.length === 0 && (
            <div className="py-10 text-center text-slate-400 text-sm bg-slate-50 rounded-lg" data-testid="ranking-empty">
              Nenhuma comissão aprovada ou paga em <strong>{ranking.label?.toLowerCase()}</strong>.
            </div>
          )}
          {!loadingRanking && ranking && ranking.items?.length > 0 && (
            <>
              <div className="overflow-x-auto" data-testid="ranking-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-[10px] uppercase tracking-[0.12em] text-slate-500">
                      <th className="text-left py-2 px-2 w-12">#</th>
                      <th className="text-left py-2 px-2">Vendedor</th>
                      <th className="text-left py-2 px-2 hidden sm:table-cell">Performance</th>
                      <th className="text-right py-2 px-2">Total</th>
                      <th className="text-right py-2 px-2 hidden md:table-cell">Qtd.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ranking.items.map((v) => {
                      const maxTotal = Math.max(...ranking.items.map((x) => x.total_comissoes)) || 1;
                      const pct = Math.round((v.total_comissoes / maxTotal) * 100);
                      return (
                        <tr
                          key={v.vendedor_id || v.posicao}
                          className="border-b border-slate-100 hover:bg-orange-50/40 transition-colors"
                          data-testid={`ranking-row-${v.posicao}`}
                        >
                          <td className="py-3 px-2 text-2xl">{MEDALHAS[v.posicao] || v.posicao}</td>
                          <td className="py-3 px-2">
                            <div className="font-semibold text-[#1B3A6B] capitalize">{v.vendedor_nome}</div>
                            <div className="text-[11px] text-slate-400 hidden sm:block">{v.vendedor_email}</div>
                          </td>
                          <td className="py-3 px-2 hidden sm:table-cell">
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden w-full max-w-[180px]">
                              <div
                                className="h-2 rounded-full transition-all"
                                style={{
                                  width: `${pct}%`,
                                  background: "linear-gradient(90deg, #F97316 0%, #fb923c 100%)",
                                }}
                              />
                            </div>
                          </td>
                          <td className="py-3 px-2 text-right font-bold text-[#F97316] whitespace-nowrap">
                            {brl(v.total_comissoes)}
                          </td>
                          <td className="py-3 px-2 text-right text-xs text-slate-500 hidden md:table-cell whitespace-nowrap">
                            {v.qtd_propostas} {v.qtd_propostas === 1 ? "comissão" : "comissões"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                <div className="text-xs text-slate-500">
                  Período: <strong className="text-slate-700">{ranking.label}</strong>
                </div>
                <div className="text-sm">
                  Total geral (Top 5): <strong className="text-[#F97316]" data-testid="ranking-total-geral">{brl(ranking.total_geral)}</strong>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
